/**
 * Chronologie de la fiche projet publique (bloc « Chronologie du projet »).
 *
 * Sortie de `ProjectDetail` (08/10/2026) pour être testée : l'ancienne
 * version empilait les étapes dans un ordre fixe, si bien que « Suivi année 1 »
 * (compté depuis la labellisation) tombait AVANT la plantation, placée l'hiver
 * suivant ou celui d'après. Désormais :
 *  — les étapes datées sont rangées dans l'ordre réel de leurs dates ; une
 *    étape sans date garde sa place logique (notification, dépôt,
 *    labellisation, plantation, suivis, audit, reconnaissance) ;
 *  — pour un projet forestier, les suivis annuels partent de la plantation ;
 *  — un projet sans arbres plantés (élevage, cultures, intrants, bâtiment,
 *    gestion d'une forêt existante) n'a pas d'étape « Plantation ».
 *
 * Module sans dépendance lourde : il part dans le paquet du navigateur.
 */
import { AGRI_TYPES } from '@/data/project-types'

/** Ce que la chronologie lit d'un projet (sous-ensemble de `ProjetAffiche`). */
export interface ProjetChronologie {
  type: string
  dateNotification?: string | null
  dateDepot?: string | null
  dateLabellisation?: string | null
  dureeProjet?: string | null
  periodeCredit?: string | null
  /**
   * Date de plantation du catalogue (JJ/MM/AAAA). `null` ou vide : aucune
   * plantation n'est inscrite pour ce projet (cas du 055, jamais planté), et
   * la chronologie ne doit alors ni dater ni marquer « faite » une plantation,
   * ni compter des suivis annuels depuis elle. Absente (`undefined`) : ancien
   * comportement, la plantation est déduite de la labellisation.
   */
  datePlantation?: string | null
}

export interface TimelineStep {
  label: string
  description: string
  /** Date affichée, déjà formatée. */
  date?: string
  /** Date réelle de l'étape (tri) ; absente : l'étape garde sa place logique. */
  quand?: Date
  done: boolean
  /** Étape en cours. */
  current?: boolean
}

/** Types dont la fiche montre une plantation d'arbres forestiers. */
const AVEC_PLANTATION = ['boisement', 'reboisement']

/** JJ/MM/AAAA → Date (ou null). */
export function parseDDMMYYYY(s: string | null | undefined): Date | null {
  if (!s) return null
  const m = s.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/)
  if (!m) return null
  const d = new Date(Number(m[3]), Number(m[2]) - 1, Number(m[1]))
  return isNaN(d.getTime()) ? null : d
}

/** « juin 2023 » / « June 2023 ». */
function fmtMonthYear(d: Date, locale: string): string {
  return d.toLocaleDateString(locale === 'en' ? 'en-US' : 'fr-FR', { month: 'long', year: 'numeric' })
}

/** « 15 juin 2023 » / « June 15, 2023 ». */
function fmtDate(d: Date, locale: string): string {
  return d.toLocaleDateString(locale === 'en' ? 'en-US' : 'fr-FR', { day: 'numeric', month: 'long', year: 'numeric' })
}

function addMonths(d: Date, months: number): Date {
  const r = new Date(d)
  r.setMonth(r.getMonth() + months)
  return r
}

/** Durée en années lue dans « 30 ans », « 5 ans (renouvelable) » ; 5 par défaut. */
function parseDuration(s: string | null | undefined): number {
  if (!s) return 5
  const m = s.match(/(\d+)\s*ans/)
  return m ? Number(m[1]) : 5
}

/**
 * Range les étapes datées dans l'ordre de leurs dates, sans déplacer les
 * étapes sans date : on trie les seules étapes datées et on les remet dans
 * les places qu'elles occupaient. Tri stable (même date : ordre logique).
 */
export function trierChronologie(steps: TimelineStep[]): TimelineStep[] {
  const places = steps.map((s, i) => (s.quand ? i : -1)).filter((i) => i >= 0)
  const datees = places.map((i) => steps[i]!).sort((a, b) => a.quand!.getTime() - b.quand!.getTime())
  const out = [...steps]
  places.forEach((place, k) => {
    out[place] = datees[k]!
  })
  return out
}

export function getTimeline(project: ProjetChronologie, locale: string, now: Date = new Date()): TimelineStep[] {
  const isFr = locale !== 'en'
  const avecPlantation = AVEC_PLANTATION.includes(project.type)
  const isAgri = AGRI_TYPES.includes(project.type)

  const dateNotif = parseDDMMYYYY(project.dateNotification)
  const dateDepot = parseDDMMYYYY(project.dateDepot)
  const dateLabel = parseDDMMYYYY(project.dateLabellisation)

  // Période de crédit : durée sur laquelle les tonnes sont reconnues (5 ans agri, 30 ans forestier)
  const creditPeriod = parseDuration(project.periodeCredit)
  const totalDuration = parseDuration(project.dureeProjet)

  // Début de la période du projet (labellisation, sinon notification)
  const startDate = dateLabel ?? dateNotif ?? null

  const steps: TimelineStep[] = []

  steps.push({
    label: isFr ? 'Notification du projet' : 'Project notification',
    description: isFr
      ? 'Le porteur de projet notifie son projet au ministère chargé de l’écologie.'
      : 'The project owner notifies the ministry of the project.',
    date: dateNotif ? fmtDate(dateNotif, locale) : undefined,
    quand: dateNotif ?? undefined,
    done: !!dateNotif,
  })

  steps.push({
    label: isFr ? 'Dépôt du dossier' : 'File submission',
    description: isFr
      ? 'Le dossier complet est déposé auprès de l’autorité compétente pour instruction.'
      : 'The complete file is submitted to the competent authority for review.',
    date: dateDepot ? fmtDate(dateDepot, locale) : undefined,
    quand: dateDepot ?? undefined,
    done: !!dateDepot,
  })

  steps.push({
    label: isFr ? 'Labellisation' : 'Labelling',
    description: isFr
      ? 'Le projet est validé et obtient le Label Bas-Carbone.'
      : 'The project is validated and receives the Label Bas-Carbone.',
    date: dateLabel ? fmtDate(dateLabel, locale) : undefined,
    quand: dateLabel ?? undefined,
    done: !!dateLabel,
  })

  // Plantation (boisement, reboisement) : l'hiver qui suit le début du projet
  // (avant octobre : l'hiver suivant ; après : celui d'après). Date prévue.
  let plantDate: Date | null = null
  // Aucune plantation inscrite au catalogue : on ne la déduit pas (14/10/2026).
  const plantationNonInscrite = project.datePlantation === null || project.datePlantation === ''
  if (avecPlantation) {
    if (startDate && !plantationNonInscrite) {
      const plantYear = startDate.getMonth() < 9 ? startDate.getFullYear() + 1 : startDate.getFullYear() + 2
      plantDate = new Date(plantYear, 0, 15)
    }
    const faite = plantDate ? now >= plantDate : false
    steps.push({
      label: isFr ? (faite || !plantDate ? 'Plantation' : 'Plantation prévue') : faite || !plantDate ? 'Planting' : 'Planned planting',
      description: isFr
        ? 'Mise en terre des essences sélectionnées sur la parcelle.'
        : 'Selected tree species are planted on the plot.',
      date: plantDate ? fmtMonthYear(plantDate, locale) : undefined,
      quand: plantDate ?? undefined,
      done: faite,
    })
  }

  // Suivi annuel : 5 premières années, à partir de la plantation pour une forêt
  const debutSuivi = avecPlantation ? plantDate : startDate
  if (debutSuivi) {
    const maxFollowUp = Math.min(creditPeriod, 5)
    const yearsSinceStart = (now.getTime() - debutSuivi.getTime()) / (365.25 * 24 * 60 * 60 * 1000)
    for (let y = 1; y <= maxFollowUp; y++) {
      const followUpDate = addMonths(debutSuivi, y * 12)
      const isPast = now >= followUpDate
      const isCurrent = !isPast && y === Math.ceil(yearsSinceStart) && yearsSinceStart > 0
      steps.push({
        label: isFr ? `Suivi année ${y}` : `Year ${y} monitoring`,
        description: isFr
          ? `Suivi des actions du projet — année ${y}/${maxFollowUp}.`
          : `Monitoring of the project actions — year ${y}/${maxFollowUp}.`,
        date: fmtMonthYear(followUpDate, locale),
        quand: followUpDate,
        done: isPast,
        current: isCurrent,
      })
    }
  }

  // Audit de vérification (six mois avant la reconnaissance), puis reconnaissance
  if (startDate) {
    const certDate = addMonths(startDate, creditPeriod * 12)
    const auditDate = addMonths(certDate, -6)
    steps.push({
      label: isFr ? 'Audit de vérification' : 'Verification audit',
      description: isFr
        ? 'Un auditeur indépendant vérifie l’atteinte des objectifs carbone du projet.'
        : 'An independent auditor verifies that the project carbon objectives have been met.',
      date: fmtMonthYear(auditDate, locale),
      quand: auditDate,
      done: now >= auditDate,
    })
    steps.push({
      label: isFr ? 'Reconnaissance des réductions' : 'Recognition of reductions',
      description: isFr
        ? 'Après vérification par un auditeur indépendant, le ministère reconnaît les réductions d’émissions, qui sont alors définitivement acquises.'
        : 'After verification by an independent auditor, the ministry recognises the emission reductions, which are then definitively acquired.',
      date: fmtMonthYear(certDate, locale),
      quand: certDate,
      done: now >= certDate,
    })
  }

  // Comptabilisation au-delà de la période de crédit (agricole, quand la durée totale la dépasse)
  if (isAgri && totalDuration > creditPeriod && startDate) {
    const accountingEnd = addMonths(startDate, totalDuration * 12)
    steps.push({
      label: isFr ? `Fin de la durée du projet (${totalDuration} ans)` : `End of project duration (${totalDuration} years)`,
      description: isFr
        ? `Le projet court sur ${totalDuration} ans au total, pour une période de crédit de ${creditPeriod} ans.`
        : `The project runs for ${totalDuration} years in total, with a ${creditPeriod}-year credit period.`,
      date: fmtMonthYear(accountingEnd, locale),
      quand: accountingEnd,
      done: now >= accountingEnd,
    })
  }

  return trierChronologie(steps)
}
